import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Check, Download, Edit, FileDown, FileText, Plus, Save, Trash2, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCurrency } from '../lib/format';

const ProductModule = ({ products, categories, onAdd, onEdit, onDelete, onAddCategory, onBulkAdd, loading, readOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ name: '', category: '', stock: 0, unit: 'Pcs', price: 0, description: '' });
  const [editingId, setEditingId] = useState(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [errors, setErrors] = useState({});
  const [importData, setImportData] = useState([]);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);

  const filteredProducts = products.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return p.name?.toLowerCase().includes(query) || 
           p.category?.toLowerCase().includes(query) ||
           p.description?.toLowerCase().includes(query);
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Nama produk wajib diisi';
    if (!formData.category.trim()) newErrors.category = 'Kategori wajib dipilih';
    if (formData.stock < 0 || formData.stock === '') newErrors.stock = 'Stok wajib diisi';
    if (!formData.unit.trim()) newErrors.unit = 'Satuan wajib dipilih';
    if (formData.price <= 0) newErrors.price = 'Harga wajib diisi dan lebih dari 0';
    if (!formData.description.trim()) newErrors.description = 'Deskripsi wajib diisi';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (editingId) {
      await onEdit({ ...formData, id: editingId });
    } else {
      await onAdd(formData);
    }
    resetForm();
  };

  const handleSaveCategory = async () => {
    if (newCategoryName.trim()) {
      await onAddCategory(newCategoryName);
      setFormData({ ...formData, category: newCategoryName });
      setIsAddingCategory(false);
      setNewCategoryName('');
      setErrors({ ...errors, category: '' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: '', stock: 0, unit: 'Pcs', price: 0, description: '' });
    setShowForm(false);
    setEditingId(null);
    setIsAddingCategory(false);
    setNewCategoryName('');
    setErrors({});
  };

  const handleEditClick = (product) => {
    setFormData(product);
    setEditingId(product.id);
    setShowForm(true);
  };

  // Download sample Excel
  const downloadSampleExcel = () => {
    import('xlsx').then(XLSX => {
      const sampleData = [
        { nama_produk: 'Laptop Gaming Pro', kategori: 'Electronics', stok: 10, satuan: 'Pcs', harga: 15000000, deskripsi: 'Laptop gaming dengan spesifikasi tinggi' },
        { nama_produk: 'Mouse Wireless RGB', kategori: 'Accessories', stok: 50, satuan: 'Pcs', harga: 250000, deskripsi: 'Mouse wireless dengan lampu RGB' },
        { nama_produk: 'Kopi Arabica Premium', kategori: 'F&B', stok: 100, satuan: 'Kg', harga: 350000, deskripsi: 'Biji kopi arabica grade A' },
        { nama_produk: 'Keyboard Mechanical', kategori: 'Accessories', stok: 25, satuan: 'Pcs', harga: 750000, deskripsi: 'Keyboard mechanical dengan switch blue' },
        { nama_produk: 'Monitor 27 inch 4K', kategori: 'Electronics', stok: 15, satuan: 'Pcs', harga: 4500000, deskripsi: 'Monitor 4K dengan refresh rate 144Hz' }
      ];

      const ws = XLSX.utils.json_to_sheet(sampleData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Produk');
      
      // Set column widths
      ws['!cols'] = [
        { wch: 25 }, // nama_produk
        { wch: 15 }, // kategori
        { wch: 10 }, // stok
        { wch: 10 }, // satuan
        { wch: 15 }, // harga
        { wch: 40 }  // deskripsi
      ];
      
      XLSX.writeFile(wb, 'sample_import_produk.xlsx');
    });
  };

  // Handle file upload (Excel)
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportError('');
    setImportData([]);

    import('xlsx').then(XLSX => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            setImportError('File kosong atau tidak ada data');
            return;
          }

          const requiredHeaders = ['nama_produk', 'kategori', 'stok', 'satuan', 'harga', 'deskripsi'];
          const headers = Object.keys(jsonData[0]).map(h => h.toLowerCase().trim());
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          
          if (missingHeaders.length > 0) {
            setImportError(`Kolom tidak lengkap. Kurang: ${missingHeaders.join(', ')}`);
            return;
          }

          const parsedData = jsonData.map(row => ({
            name: String(row.nama_produk || row.Nama_Produk || '').trim(),
            category: String(row.kategori || row.Kategori || '').trim(),
            stock: parseInt(row.stok || row.Stok) || 0,
            unit: String(row.satuan || row.Satuan || 'Pcs').trim(),
            price: parseInt(row.harga || row.Harga) || 0,
            description: String(row.deskripsi || row.Deskripsi || '').trim()
          })).filter(item => item.name);

          if (parsedData.length === 0) {
            setImportError('Tidak ada data valid ditemukan');
            return;
          }

          setImportData(parsedData);
        } catch (err) {
          setImportError('Gagal membaca file. Pastikan format Excel (.xlsx) benar.');
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Import products with duplicate check (add stock only if same product)
  const handleImport = async () => {
    if (importData.length === 0) return;
    
    setImporting(true);
    let imported = 0;
    let updated = 0;
    
    try {
      // Track processed products to detect duplicates within the same import batch
      const processedKeys = new Set();
      const currentProducts = [...products];
      
      for (const product of importData) {
        const key = `${product.name.toLowerCase()}|${product.category.toLowerCase()}|${product.price}|${(product.description || '').toLowerCase()}`;
        
        // Check against existing products AND already processed in this batch
        const existingInDB = currentProducts.find(p => 
          p.name.toLowerCase() === product.name.toLowerCase() &&
          p.category.toLowerCase() === product.category.toLowerCase() &&
          p.price === product.price &&
          (p.description || '').toLowerCase() === (product.description || '').toLowerCase()
        );
        
        if (existingInDB || processedKeys.has(key)) {
          if (existingInDB) {
            // Update stock via edit
            const newStock = existingInDB.stock + product.stock;
            await onEdit({ ...existingInDB, stock: newStock });
            existingInDB.stock = newStock; // Update local tracking
          } else {
            // Duplicate within import batch - just call onAdd, backend handles dedup
            await onAdd(product);
          }
          updated++;
        } else {
          await onAdd(product);
          // Track this product as processed
          currentProducts.push(product);
          imported++;
        }
        processedKeys.add(key);
      }
      
      setShowImportModal(false);
      setImportData([]);
      setImportError('');
      
      // Show result message
      const message = [];
      if (imported > 0) message.push(`${imported} produk baru ditambahkan`);
      if (updated > 0) message.push(`${updated} produk stoknya diupdate`);
      if (message.length > 0) {
        alert(message.join(', '));
      }
    } catch (err) {
      setImportError('Gagal mengimport beberapa produk');
    }
    setImporting(false);
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Produk</h2>
          <p className="text-muted-foreground mt-1">Kelola katalog produk Anda</p>
        </div>
        {!readOnly && (
        <div className="flex gap-2">
          <button 
            onClick={() => setShowImportModal(true)} 
            className="btn-secondary"
            data-testid="import-product-btn"
          >
            <FileDown size={18}/> Import
          </button>
          <button 
            onClick={() => showForm ? resetForm() : setShowForm(true)} 
            className={showForm ? "btn-secondary" : "btn-primary"}
            data-testid="add-product-btn"
          >
            {showForm ? <><X size={18}/> Batal</> : <><Plus size={18}/> Tambah Produk</>}
          </button>
        </div>
        )}
      </div>

      {/* Import Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowImportModal(false)} />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-dropdown w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 pb-0 mb-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-xl">
                    <FileDown className="text-emerald-600" size={20} />
                  </div>
                  <h3 className="text-lg font-heading font-bold">Import Produk</h3>
                </div>
                <button onClick={() => { setShowImportModal(false); setImportData([]); setImportError(''); }} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 space-y-4">
                {/* Download Sample */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-800">Download Sample File</p>
                      <p className="text-sm text-blue-600 mt-1">Download contoh file Excel untuk melihat format yang benar</p>
                    </div>
                    <button 
                      onClick={downloadSampleExcel}
                      className="btn-primary bg-blue-500 hover:bg-blue-600"
                      data-testid="download-sample-btn"
                    >
                      <FileDown size={16}/> Download Sample
                    </button>
                  </div>
                </div>

                {/* Info duplikat */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Info:</strong> Jika produk dengan nama, kategori, harga, dan deskripsi yang sama sudah ada, 
                    maka hanya stok yang akan ditambahkan (tidak membuat produk duplikat).
                  </p>
                </div>

                {/* Upload File */}
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                  <input 
                    type="file" 
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-upload"
                    data-testid="excel-upload-input"
                  />
                  <label htmlFor="excel-upload" className="cursor-pointer">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <FileText className="text-slate-400" size={24} />
                    </div>
                    <p className="font-medium text-slate-700">Klik untuk upload file Excel (.xlsx)</p>
                    <p className="text-sm text-slate-400 mt-1">atau drag & drop file disini</p>
                  </label>
                </div>

                {/* Error */}
                {importError && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <p className="text-sm text-rose-600">{importError}</p>
                  </div>
                )}

                {/* Preview */}
                {importData.length > 0 && (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                      <p className="font-medium text-sm text-slate-700">{importData.length} produk siap diimport</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Nama</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Kategori</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Stok</th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Harga</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importData.map((item, idx) => (
                            <tr key={idx} className="border-t border-slate-100">
                              <td className="px-4 py-2 font-medium">{item.name}</td>
                              <td className="px-4 py-2 text-slate-600">{item.category}</td>
                              <td className="px-4 py-2 text-right">{item.stock} {item.unit}</td>
                              <td className="px-4 py-2 text-right font-mono">{formatCurrency(item.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 p-6 pt-4 border-t border-slate-100 flex-shrink-0 bg-white rounded-b-2xl">
                <button 
                  onClick={() => { setShowImportModal(false); setImportData([]); setImportError(''); }} 
                  className="btn-secondary flex-1"
                >
                  Batal
                </button>
                <button 
                  onClick={handleImport} 
                  className="btn-primary flex-1"
                  disabled={importData.length === 0 || importing}
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Mengimport...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2"><Check size={16}/> Import {importData.length} Produk</span>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Bar */}
      <div className="card p-4">
        <div className="relative w-full lg:w-80">
          <input
            type="text"
            placeholder="Cari produk..."
            className="input-field pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-product-input"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {showForm && !readOnly && (
          <motion.form 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit} 
            className="card p-6 space-y-6"
            data-testid="product-form"
          >
            <h3 className="font-heading font-semibold text-lg border-b border-border pb-4">
              {editingId ? 'Edit Produk' : 'Tambah Produk Baru'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex flex-col space-y-2">
                <label className="label">Nama Produk <span className="text-destructive">*</span></label>
                <input 
                  className={`input-field ${errors.name ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  value={formData.name} 
                  onChange={e => { setFormData({...formData, name: e.target.value}); setErrors({...errors, name: ''}); }}
                  placeholder="Nama produk" 
                />
                {errors.name && <span className="text-xs text-destructive">{errors.name}</span>}
              </div>
              
              <div className="flex flex-col space-y-2">
                <label className="label">Kategori <span className="text-destructive">*</span></label>
                {isAddingCategory ? (
                  <div className="flex gap-2">
                    <input 
                      autoFocus
                      className="input-field flex-1"
                      placeholder="Nama kategori baru"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                    />
                    <button type="button" onClick={handleSaveCategory} className="btn-primary px-3"><Check size={18}/></button>
                    <button type="button" onClick={() => setIsAddingCategory(false)} className="btn-secondary px-3"><X size={18}/></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select 
                      className={`input-field bg-white flex-1 ${errors.category ? 'border-destructive focus:ring-destructive/50' : ''}`}
                      value={formData.category} 
                      onChange={e => { setFormData({...formData, category: e.target.value}); setErrors({...errors, category: ''}); }}
                    >
                      <option value="">-- Pilih Kategori --</option>
                      {categories.map((c, idx) => <option key={idx} value={c}>{c}</option>)}
                    </select>
                    <button type="button" onClick={() => setIsAddingCategory(true)} className="btn-secondary px-3" title="Tambah Kategori"><Plus size={18}/></button>
                  </div>
                )}
                {errors.category && <span className="text-xs text-destructive">{errors.category}</span>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-2">
                  <label className="label">Stok Awal <span className="text-destructive">*</span></label>
                  <input 
                    type="number"
                    className={`input-field ${errors.stock ? 'border-destructive focus:ring-destructive/50' : ''}`}
                    value={formData.stock} 
                    onChange={e => { setFormData({...formData, stock: parseInt(e.target.value) || 0}); setErrors({...errors, stock: ''}); }}
                  />
                  {errors.stock && <span className="text-xs text-destructive">{errors.stock}</span>}
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="label">Satuan <span className="text-destructive">*</span></label>
                  <select 
                    className={`input-field bg-white ${errors.unit ? 'border-destructive focus:ring-destructive/50' : ''}`}
                    value={formData.unit} 
                    onChange={e => { setFormData({...formData, unit: e.target.value}); setErrors({...errors, unit: ''}); }}
                  >
                    <option value="Pcs">Pcs</option>
                    <option value="Kg">Kg</option>
                    <option value="Box">Box</option>
                    <option value="Ltr">Ltr</option>
                    <option value="Unit">Unit</option>
                  </select>
                  {errors.unit && <span className="text-xs text-destructive">{errors.unit}</span>}
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <label className="label">Harga Satuan <span className="text-destructive">*</span></label>
                <input 
                  type="number"
                  className={`input-field ${errors.price ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  value={formData.price} 
                  onChange={e => { setFormData({...formData, price: parseInt(e.target.value) || 0}); setErrors({...errors, price: ''}); }}
                />
                {errors.price && <span className="text-xs text-destructive">{errors.price}</span>}
              </div>
              
              <div className="md:col-span-2 flex flex-col space-y-2">
                <label className="label">Deskripsi <span className="text-destructive">*</span></label>
                <textarea 
                  className={`input-field resize-none ${errors.description ? 'border-destructive focus:ring-destructive/50' : ''}`}
                  rows="3" 
                  value={formData.description} 
                  onChange={e => { setFormData({...formData, description: e.target.value}); setErrors({...errors, description: ''}); }}
                  placeholder="Deskripsi produk..." 
                />
                {errors.description && <span className="text-xs text-destructive">{errors.description}</span>}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button type="submit" className="btn-primary" data-testid="save-product-btn">
                <Save size={16}/> {editingId ? 'Perbarui' : 'Simpan'}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left">No</th>
                <th className="px-6 py-4 text-left">Nama Produk</th>
                <th className="px-6 py-4 text-left">Kategori</th>
                <th className="px-6 py-4 text-right">Harga</th>
                <th className="px-6 py-4 text-center">Stok</th>
                <th className="px-6 py-4 text-left">Deskripsi</th>
                {!readOnly && <th className="px-6 py-4 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={readOnly ? "6" : "7"} className="px-6 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
              ) : filteredProducts.length === 0 ? (
                <tr><td colSpan={readOnly ? "6" : "7"} className="px-6 py-12 text-center text-muted-foreground">{searchQuery ? 'Tidak ada hasil ditemukan.' : 'Belum ada produk.'}</td></tr>
              ) : filteredProducts.map((p, index) => (
                <tr key={`product-${p.id}-${index}`} className="table-row">
                  <td className="px-6 py-4 text-muted-foreground">{index + 1}</td>
                  <td className="px-6 py-4 font-medium">{p.name}</td>
                  <td className="px-6 py-4"><span className="px-2 py-1 bg-slate-100 rounded text-xs">{p.category || '-'}</span></td>
                  <td className="px-6 py-4 text-right font-mono">{formatCurrency(p.price)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`font-bold ${p.stock < 5 ? 'text-destructive' : ''}`}>{p.stock}</span>
                    <span className="text-xs text-muted-foreground ml-1">{p.unit}</span>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground truncate max-w-xs text-xs">{p.description || '-'}</td>
                  <td className="px-6 py-4">
                    {!readOnly && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleEditClick(p)} className="p-2 hover:bg-slate-100 rounded-lg text-muted-foreground hover:text-primary transition-colors"><Edit size={16}/></button>
                      <button onClick={() => onDelete(p.id)} className="p-2 hover:bg-rose-50 rounded-lg text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16}/></button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== SUPPLIER MODULE ==============


export default ProductModule;
